import React, { useEffect, useRef, useState } from "react";
import { stripMathDelimiters } from "@ever-tutor/et-ui";
import { useUIStore } from "@/features/tutor/store/useUIStore.js";
import { renderCachedCaptionMath } from "@/features/tutor/utils/captionMathCache";
import { useViewport } from "@/hooks/useViewport";
import { noTranslateProps } from "@/utils/pageTranslation";
import { useCaptionsUnavailable } from "@/features/tutor/hooks/useCaptionsUnavailable";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useForm } from "@/features/tutor/context/FormContext";
import { startsNewCaptionRun } from "@/features/tutor/utils/captionPill";
import {
  selectIsNotepadOpen,
  useNotepadStore,
} from "@/features/notepad/store/useNotepadStore";

const CAPTION_IDLE_MS = 5000;
const CAPTION_FADE_MS = 4200;
const CAPTION_FADE_INTERACTIVE_MS = 2100;
const CAPTION_RESTORE_MS = 150;
const CONTROL_FADE_MS = 240;
const CAPTION_EDGE_GAP = 24;
const CAPTION_MIN_WIDTH = 240;
const PILL_FADE_MS = 120;
const DEFAULT_PILL_MOVE_MS = 200;

const PILL_MOVE_MS_BY_SPEED = {
  slow: 300,
  medium: 250,
  fast: 200,
};

const Captions = () => {
  const { formData } = useForm();
  const pillMoveMs =
    PILL_MOVE_MS_BY_SPEED[formData.voice?.speed] ?? DEFAULT_PILL_MOVE_MS;
  const captionsUnavailable = useCaptionsUnavailable();
  const chatWindowOpen = useChatSettingsStore((state) => state.chatWindowOpen);
  const isNotepadOpen = useNotepadStore(selectIsNotepadOpen);
  const captionsOpen = useUIStore((state) => state.captionsOpen);
  const currentCaptions = useUIStore((state) => state.currentCaptions);
  const setCurrentCaptions = useUIStore((state) => state.setCurrentCaptions);
  const lastAgentResponse = useUIStore((state) => state.lastAgentResponse);
  const captionsFrozen = useUIStore((state) => state.captionsFrozen);
  const captionsExpanded = useUIStore((state) => state.captionsExpanded);
  const setCaptionsFrozen = useUIStore((state) => state.setCaptionsFrozen);
  const setCaptionsExpanded = useUIStore((state) => state.setCaptionsExpanded);
  const { isMobilePortrait, isMobileLandscape } = useViewport();

  // Track #main-screen rect for precise positioning
  const [mainRect, setMainRect] = useState(null);

  useEffect(() => {
    const el = document.getElementById("main-screen");
    if (!el) return;

    const update = () => setMainRect(el.getBoundingClientRect());
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Clear any stale captions left over from a previous session on mount
  useEffect(() => {
    setCurrentCaptions([]);
  }, [setCurrentCaptions]);

  const [isFading, setIsFading] = useState(false);
  const [fadePassthrough, setFadePassthrough] = useState(false);
  const [keepAlive, setKeepAlive] = useState(0);

  useEffect(() => {
    setIsFading(false);
    setFadePassthrough(false);
    if (!captionsFrozen || captionsExpanded) return;

    const id = setTimeout(() => setIsFading(true), CAPTION_IDLE_MS);
    return () => clearTimeout(id);
  }, [captionsFrozen, captionsExpanded, keepAlive]);

  useEffect(() => {
    if (!isFading) return;

    const passthrough = setTimeout(
      () => setFadePassthrough(true),
      CAPTION_FADE_INTERACTIVE_MS,
    );
    const collapse = setTimeout(() => {
      if (!useUIStore.getState().captionsFrozen) return;
      setCurrentCaptions([]);
      setCaptionsFrozen(false);
      setCaptionsExpanded(false);
      // Keep lastAgentResponse so the restore arrow remains available until TURN_CHANGE
    }, CAPTION_FADE_MS);

    return () => {
      clearTimeout(passthrough);
      clearTimeout(collapse);
    };
  }, [isFading, setCurrentCaptions, setCaptionsFrozen, setCaptionsExpanded]);

  const keepVisible = () => {
    if (!isFading) return;
    setKeepAlive((n) => n + 1);
  };

  const handleDismiss = () => {
    setCurrentCaptions([]);
    setCaptionsFrozen(false);
    setCaptionsExpanded(false);
    // Keep lastAgentResponse so the restore arrow remains available until TURN_CHANGE
  };

  const hasContent =
    currentCaptions?.length > 0 || (captionsFrozen && lastAgentResponse);

  const captionsSnapshots = useUIStore((state) => state.captionsSnapshots);

  // Expanded view shows the full AI response — all accumulated sentence snapshots.
  // This preserves KaTeX tokens as-is (no string splitting).
  const { expandedWords, hasLeadingSentence } = React.useMemo(() => {
    if (!currentCaptions?.length)
      return { expandedWords: currentCaptions, hasLeadingSentence: false };

    if (!captionsSnapshots?.length)
      return { expandedWords: currentCaptions, hasLeadingSentence: false };

    const allWords = captionsSnapshots.reduce((acc, snap, i) => {
      if (i > 0) {
        const prevSnap = captionsSnapshots[i - 1];
        const lastWord = prevSnap[prevSnap.length - 1] ?? "";
        // -- splits leave no sentence-ending punctuation → insert ellipsis
        if (!/[.!?]$/.test(lastWord)) acc.push("...");
      }
      return acc.concat(snap);
    }, []);
    const allFlat = captionsSnapshots.flat();
    return {
      expandedWords: allWords,
      hasLeadingSentence: allFlat.join(" ") !== currentCaptions.join(" "),
    };
  }, [captionsSnapshots, currentCaptions]);

  const showExpandButton =
    captionsFrozen && lastAgentResponse && hasLeadingSentence;

  const displayWords = captionsExpanded ? expandedWords : currentCaptions;

  if (captionsUnavailable || chatWindowOpen) return null;

  // If we have the main-screen rect, position relative to it
  if (mainRect) {
    const bottomOffset = isNotepadOpen ? CAPTION_EDGE_GAP : 20;

    const centerX = window.innerWidth / 2;
    const bottomY = mainRect.bottom - bottomOffset;

    const captionMaxWidth = Math.max(
      2 *
        Math.min(
          centerX - mainRect.left - CAPTION_EDGE_GAP,
          mainRect.right - centerX - CAPTION_EDGE_GAP,
        ),
      CAPTION_MIN_WIDTH,
    );

    if (!captionsOpen || !hasContent) return null;

    return (
      <div
        {...noTranslateProps("fixed z-[1100]")}
        data-captions-surface
        onPointerDown={keepVisible}
        style={{
          left: centerX,
          top: bottomY,
          transform: "translate(-50%, -100%)",
          opacity: isFading ? 0 : 1,
          pointerEvents: fadePassthrough ? "none" : "auto",
          transition: `opacity ${
            isFading ? CAPTION_FADE_MS : CAPTION_RESTORE_MS
          }ms linear, left 300ms, top 300ms`,
          maxWidth: captionMaxWidth,
          ...(captionsExpanded && {
            width: captionMaxWidth,
            display: "flex",
            justifyContent: "center",
          }),
        }}
      >
        <div className="relative inline-block">
          <button
            onClick={handleDismiss}
            className="absolute -m-3 right-2 top-2 z-10 flex items-center justify-center rounded-xl p-3 text-xs leading-none text-white"
            aria-label="Dismiss captions"
            style={{
              opacity: captionsFrozen ? 1 : 0,
              pointerEvents: captionsFrozen ? "auto" : "none",
              transition: `opacity ${CONTROL_FADE_MS}ms`,
            }}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L9 9M9 1L1 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            onClick={() => setCaptionsExpanded(!captionsExpanded)}
            className="absolute -m-3 left-1/2 top-2 z-10 -translate-x-1/2 rounded-xl p-3 text-white"
            aria-label={
              captionsExpanded ? "Collapse captions" : "Show full sentence"
            }
            style={{
              opacity: showExpandButton ? 1 : 0,
              pointerEvents: showExpandButton ? "auto" : "none",
              transition: `opacity ${CONTROL_FADE_MS}ms`,
            }}
          >
            <svg
              width="10"
              height="6"
              viewBox="0 0 20 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: captionsExpanded ? "rotate(180deg)" : "none",
              }}
            >
              <path
                d="M1 11L10 2L19 11"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <CaptionContent
            currentCaptions={displayWords}
            isMobilePortrait={isMobilePortrait}
            isMobileLandscape={isMobileLandscape}
            isExpanded={captionsExpanded}
            expandedWidth={captionMaxWidth}
            freezeHighlight={showExpandButton}
            pillMoveMs={pillMoveMs}
          />
        </div>
      </div>
    );
  }

  // Rect not yet measured — skip render
  return null;
};

function parseMathWord(word) {
  const stripped = stripMathDelimiters(word);
  if (!stripped) return null;
  return { latex: stripped.latex, suffix: stripped.trailing };
}

function CaptionContent({
  currentCaptions,
  isMobilePortrait,
  isMobileLandscape,
  isExpanded,
  expandedWidth,
  freezeHighlight,
  pillMoveMs,
}) {
  const spanRefsRef = useRef([]);
  const pillRef = useRef(null);
  const prevLengthRef = useRef(0);
  const prevFirstRef = useRef(null);

  useEffect(() => {
    const pill = pillRef.current;
    if (!pill) return;

    if (isExpanded || !currentCaptions.length) {
      pill.style.transition = "none";
      pill.style.opacity = "0";
      prevLengthRef.current = 0;
      prevFirstRef.current = null;
      return;
    }

    if (freezeHighlight) {
      pill.style.transition = `opacity ${PILL_FADE_MS * 4}ms`;
      pill.style.opacity = "0";
      prevLengthRef.current = 0;
      prevFirstRef.current = null;
      return;
    }

    const lastIndex = currentCaptions.length - 1;
    const isFirstWord = startsNewCaptionRun(
      currentCaptions,
      prevLengthRef.current,
      prevFirstRef.current,
    );
    prevLengthRef.current = currentCaptions.length;
    prevFirstRef.current = currentCaptions[0];

    if (isFirstWord) {
      pill.style.transition = "none";
      pill.style.opacity = "0";
    }

    const applyPill = (isFirst) => {
      const span = spanRefsRef.current[lastIndex];
      if (!span) return;
      const spanLeft = span.offsetLeft;
      const spanRight = span.offsetLeft + span.offsetWidth;

      const snapToSpan = () => {
        pill.style.transition = "none";
        pill.style.left = spanLeft + "px";
        pill.style.width = span.offsetWidth + "px";
        pill.style.top = span.offsetTop + "px";
        pill.style.height = span.offsetHeight + "px";
        pill.style.opacity = "0";
        pill.offsetHeight; // force reflow
        pill.style.transition = `opacity ${PILL_FADE_MS}ms`;
        pill.style.opacity = "1";
      };

      if (isFirst) {
        snapToSpan();
      } else {
        // Read actual animated position (mid-animation safe)
        const computed = getComputedStyle(pill);
        const currentTop = parseFloat(computed.top) || 0;

        // New word is on a different line — snap instead of stretch
        if (Math.abs(currentTop - span.offsetTop) > 2) {
          snapToSpan();
          return;
        }

        const currentLeft = parseFloat(computed.left) || 0;
        const newWidth = spanRight - currentLeft;

        pill.style.transition = "none";
        pill.style.left = currentLeft + "px";
        pill.style.width = newWidth + "px";
        pill.style.top = span.offsetTop + "px";
        pill.style.height = span.offsetHeight + "px";
        pill.offsetHeight; // force reflow

        pill.style.transition = `left ${pillMoveMs}ms ease-out, width ${pillMoveMs}ms ease-out, top ${pillMoveMs}ms ease-out, height ${pillMoveMs}ms ease-out, opacity ${PILL_FADE_MS}ms`;
        pill.style.left = spanLeft + "px";
        pill.style.width = span.offsetWidth + "px";
        pill.style.opacity = "1";
      }
    };

    // double-rAF: first frame renders the span into DOM, second frame reads layout
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => applyPill(isFirstWord));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [
    currentCaptions,
    isExpanded,
    freezeHighlight,
    pillMoveMs,
    isMobilePortrait,
    isMobileLandscape,
    expandedWidth,
  ]);

  return (
    <div
      className={`font-mulish text-white ${isExpanded ? (isMobilePortrait ? "text-sm" : isMobileLandscape ? "text-base" : "text-[22px]") : isMobilePortrait ? "text-sm max-w-[260px]" : isMobileLandscape ? "text-base max-w-[380px]" : "text-[22px] max-w-[560px]"}`}
      style={isExpanded ? { maxWidth: expandedWidth } : undefined}
    >
      <div
        className={`relative bg-black/50 rounded-lg px-6 py-3 leading-relaxed ${isExpanded ? "block" : "inline-block"}`}
      >
        {!isExpanded && (
          <div
            className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none"
            style={{ zIndex: 0 }}
          >
            <div
              ref={pillRef}
              className="absolute rounded-md"
              style={{
                backgroundColor: "hsl(152, 55%, 38%)",
                opacity: 0,
              }}
            />
          </div>
        )}
        {currentCaptions.map((word, index) => {
          const mathParsed = parseMathWord(word);
          const isMath = mathParsed !== null;
          const isPunctuation = !isMath && /^[.!?,;:]+$/.test(word);
          const nextIsPunctuation = /^[.!?,;:]+$/.test(
            currentCaptions[index + 1] ?? "",
          );
          const isLast = index === currentCaptions.length - 1;
          const spanStyle = isExpanded
            ? {
                padding: isPunctuation ? "0.1em 0" : "0.1em 0.15em",
                marginLeft: isPunctuation ? "0" : "-0.15em",
                marginRight: isLast ? "0" : "-0.15em",
                backgroundColor: "transparent",
              }
            : {
                position: "relative",
                zIndex: 1,
                animationDuration: "0.05s",
                padding: isPunctuation ? "0.1em 0" : "0.1em 0.15em",
                marginLeft: isPunctuation ? "0" : "-0.15em",
                marginRight: isLast ? "0" : "-0.15em",
              };
          const spanRef = isExpanded
            ? undefined
            : (el) => {
                spanRefsRef.current[index] = el;
              };

          const mathHtml = isMath
            ? renderCachedCaptionMath(mathParsed.latex)
            : null;

          if (isMath && mathHtml) {
            return (
              <React.Fragment key={index}>
                <span
                  ref={spanRef}
                  className="rounded-md animate-fade-in-right inline-block katex-caption"
                  style={spanStyle}
                  // safe: renderCaptionMath sanitizes via DOMPurify
                  dangerouslySetInnerHTML={{ __html: mathHtml }}
                />
                {mathParsed.suffix || null}
                {!isLast && !nextIsPunctuation ? " " : null}
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={index}>
              <span
                ref={spanRef}
                className="rounded-md animate-fade-in-right inline-block"
                style={spanStyle}
              >
                {word}
              </span>
              {!isLast && !nextIsPunctuation ? " " : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default Captions;
